// React
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Auth
import { clearApiSession, refreshApiSession } from "Auth/apiSession";
import { mbsAuth } from "./mbsAuth";

// Constants
import { AUTH_STATUS } from "Constants/AuthConstants";

const MBSAuthContext = createContext(null);

export const MBSAuthProvider = ({ children }) => {
  const [status, setStatus] = useState(AUTH_STATUS.LOADING);
  const [user, setUser] = useState(null);
  const [requiredAttributes, setRequiredAttributes] = useState([]);
  const statusRef = useRef(AUTH_STATUS.LOADING);

  const updateStatus = useCallback((nextStatus) => {
    statusRef.current = nextStatus;
    setStatus(nextStatus);
  }, []);

  const resetAuthState = useCallback(() => {
    if (statusRef.current === AUTH_STATUS.NEW_PASSWORD_REQUIRED) {
      return;
    }
    clearApiSession();
    setUser(null);
    setRequiredAttributes([]);
    updateStatus(AUTH_STATUS.UNAUTHENTICATED);
  }, [updateStatus]);

  const refreshSession = useCallback(async () => {
    try {
      const currentUser = await mbsAuth.getCurrentUserProfile();
      if (currentUser) {
        await refreshApiSession();
        setUser(currentUser);
        setRequiredAttributes([]);
        updateStatus(AUTH_STATUS.AUTHENTICATED);
      } else {
        resetAuthState();
      }
    } catch {
      resetAuthState();
    }
  }, [resetAuthState, updateStatus]);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const signIn = useCallback(
    async (userEmail, password) => {
      const result = await mbsAuth.signIn(userEmail, password);
      if (result.challengeName === "NEW_PASSWORD_REQUIRED") {
        setRequiredAttributes(result.missingAttributes);
        updateStatus(AUTH_STATUS.NEW_PASSWORD_REQUIRED);
        return result;
      }
      await refreshSession();
      return result;
    },
    [refreshSession, updateStatus]
  );

  const signInWithSso = useCallback(async (userEmail) => {
    return mbsAuth.signInWithSso(userEmail);
  }, []);

  const confirmNewPassword = useCallback(
    async (newPassword, userAttributes = {}) => {
      const result = await mbsAuth.confirmNewPassword(
        newPassword,
        userAttributes
      );
      await refreshSession();
      return result;
    },
    [refreshSession]
  );

  const forgotPassword = useCallback(async (userEmail) => {
    return mbsAuth.forgotPassword(userEmail);
  }, []);

  const confirmForgotPassword = useCallback(
    async (userEmail, code, newPassword) => {
      return mbsAuth.confirmForgotPassword(userEmail, code, newPassword);
    },
    []
  );

  const signOut = useCallback(async () => {
    await mbsAuth.signOut();
    resetAuthState();
  }, [resetAuthState]);

  const value = useMemo(
    () => ({
      user,
      status,
      requiredAttributes,
      isAuthenticated: status === AUTH_STATUS.AUTHENTICATED,
      isLoading: status === AUTH_STATUS.LOADING,
      signIn,
      signInWithSso,
      confirmNewPassword,
      forgotPassword,
      confirmForgotPassword,
      signOut,
    }),
    [
      confirmForgotPassword,
      confirmNewPassword,
      forgotPassword,
      requiredAttributes,
      signIn,
      signInWithSso,
      signOut,
      status,
      user,
    ]
  );

  return (
    <MBSAuthContext.Provider value={value}>{children}</MBSAuthContext.Provider>
  );
};

export const useMBSAuth = () => {
  const context = useContext(MBSAuthContext);
  if (!context) {
    throw new Error("useMBSAuth must be used within an MBSAuthProvider.");
  }
  return context;
};
